import { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { calculateCompoundInterest, calculateCredit } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { exportToCSV } from '../../utils/formatters';
import { TableIcon, Download, FileText, PauseCircle, TrendingUp, TrendingDown } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const BreakdownTable = () => {
  const { inputs, investmentType, t, language, formatMoney } = useApp();
  const isCredit = investmentType === INVESTMENT_TYPES.CREDIT;
  const isAccumLike = investmentType === INVESTMENT_TYPES.ACCUMULATION_PLAN ||
    investmentType === INVESTMENT_TYPES.CHILD_FUTURE;

  const { yearlyBreakdown, creditBreakdown } = useMemo(() => {
    if (isCredit) {
      const res = calculateCredit({
        loanAmount: inputs.loanAmount ?? 200000,
        annualInterestRate: inputs.creditRate ?? 5,
        termMonths: inputs.creditTermMonths ?? 300,
        monthlyFeeRate: inputs.monthlyFeeRate ?? 0.03,
        gracePeriodMonths: inputs.gracePeriodMonths ?? 0,
        repaymentType: inputs.repaymentType ?? 'annuity',
        extraMonthlyPayment: inputs.extraMonthlyPayment ?? 0,
      });
      return { yearlyBreakdown: [], creditBreakdown: res.monthlyBreakdown || [] };
    }
    const taxConfig = getTaxConfig(investmentType);
    const res = calculateCompoundInterest({
      initialDeposit: inputs.initialDeposit,
      monthlyContribution: inputs.monthlyContribution,
      annualInterestRate: inputs.annualInterestRate,
      years: inputs.years,
      compoundingFrequency: inputs.compoundingFrequency,
      contributionFrequency: inputs.contributionFrequency,
      taxRate: inputs.includeTaxes ? taxConfig.interestTaxRate : 0,
      taxOnEachPeriod: taxConfig.taxOnEachPeriod,
      adminFeeRate: isAccumLike ? inputs.adminFeeRate : 0,
      pauseStartYear: inputs.pauseEnabled ? inputs.pauseStartYear : 0,
      pauseEndYear: inputs.pauseEnabled ? inputs.pauseEndYear : 0,
      useSimpleInterest: taxConfig.useSimpleInterest,
      topUps: inputs.topUps || [],
      withdrawals: inputs.withdrawals || [],
    });
    return { yearlyBreakdown: res.yearlyBreakdown, creditBreakdown: [] };
  }, [inputs, isCredit, isAccumLike]);

  // ─── Labels per language ──────────────────────────────────────────────────
  const lbl = {
    year:    language === 'ro' ? 'An'             : 'Year',
    month:   language === 'ro' ? 'Lună'           : 'Month',
    contribs:language === 'ro' ? 'Total contribuții' : 'Total contributions',
    interest:language === 'ro' ? 'Dobândă'        : 'Interest',
    interestNet: language === 'ro' ? 'Dobândă netă' : 'Net interest',
    tax:     language === 'ro' ? 'Impozit'        : 'Tax',
    adminFee:language === 'ro' ? 'Comision adm.'  : 'Admin fee',
    topUp:   language === 'ro' ? 'Top-up'         : 'Top-up',
    withdraw:language === 'ro' ? 'Retragere'      : 'Withdrawal',
    total:   language === 'ro' ? 'Total acumulat' : 'Total accumulated',
    payment: language === 'ro' ? 'Rată totală'    : 'Total payment',
    principal:language==='ro'  ? 'Principal'      : 'Principal',
    intCol:  language === 'ro' ? 'Dobândă'        : 'Interest',
    fee:     language === 'ro' ? 'Comision'       : 'Fee',
    balance: language === 'ro' ? 'Sold rămas'     : 'Remaining balance',
  };

  // ─── CSV Export ───────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (isCredit) {
      const headers = [
        { key: 'month',    label: lbl.month },
        { key: 'payment',  label: lbl.payment },
        { key: 'principal',label: lbl.principal },
        { key: 'interest', label: lbl.intCol },
        { key: 'fee',      label: lbl.fee },
        { key: 'balance',  label: lbl.balance },
      ];
      const filename = language === 'ro' ? 'scadentar-credit' : 'amortization-schedule';
      exportToCSV(creditBreakdown, filename, headers, language);
    } else {
      const headers = [
        { key: 'year',               label: lbl.year },
        { key: 'totalContributions', label: lbl.contribs },
        { key: 'interestNet',        label: inputs.includeTaxes ? lbl.interestNet : lbl.interest },
        ...(inputs.includeTaxes ? [{ key: 'taxPaid', label: lbl.tax }] : []),
        ...(isAccumLike ? [{ key: 'adminFee', label: lbl.adminFee }] : []),
        ...(inputs.topUps?.length ? [{ key: 'topUp', label: lbl.topUp }] : []),
        ...(inputs.withdrawals?.length ? [{ key: 'withdrawal', label: lbl.withdraw }] : []),
        { key: 'endBalance', label: lbl.total },
      ];
      const typeNames = {
        [INVESTMENT_TYPES.SAVINGS_ACCOUNT]:   language === 'ro' ? 'cont-economii'      : 'savings-account',
        [INVESTMENT_TYPES.ACCUMULATION_PLAN]: language === 'ro' ? 'plan-acumulare'     : 'accumulation-plan',
        [INVESTMENT_TYPES.CHILD_FUTURE]:      language === 'ro' ? 'viitorul-copilului' : 'child-future-plan',
      };
      const filename = typeNames[investmentType] || 'investitie';
      const data = yearlyBreakdown.map(r => ({ ...r, interestNet: r.totalInterestGross - r.totalTaxPaid }));
      exportToCSV(data, filename, headers, language);
    }
  };

  // ─── PDF Export ───────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF();

    const typeNames = {
      [INVESTMENT_TYPES.SAVINGS_ACCOUNT]:   language === 'ro' ? 'Cont de Economii'            : 'Savings Account',
      [INVESTMENT_TYPES.ACCUMULATION_PLAN]: language === 'ro' ? 'Simulator Plan de Acumulare'  : 'Accumulation Plan',
      [INVESTMENT_TYPES.CHILD_FUTURE]:      language === 'ro' ? 'Viitorul Copilului'           : "Child's Future Plan",
      [INVESTMENT_TYPES.CREDIT]:            language === 'ro' ? 'Simulator Credit'             : 'Loan Simulator',
    };

    const title = isCredit
      ? (language === 'ro' ? 'Scadențar Credit — MoneySmart.ro' : 'Amortization Schedule — MoneySmart.ro')
      : `${typeNames[investmentType]} — MoneySmart.ro`;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 18);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text('moneysmart.ro', 14, 24);
    doc.setTextColor(0);

    let yPos = 32;

    if (isCredit) {
      const lines = [
        `${t('loanAmount')}: ${formatMoney(inputs.loanAmount)}`,
        `${language === 'ro' ? 'Rata dobânzii' : 'Interest rate'}: ${inputs.creditRate}%`,
        `${language === 'ro' ? 'Perioadă' : 'Term'}: ${inputs.creditTermMonths} ${language === 'ro' ? 'luni' : 'months'}`,
        `${language === 'ro' ? 'Mod rambursare' : 'Repayment'}: ${(inputs.repaymentType ?? 'annuity') === 'annuity' ? (language === 'ro' ? 'Rate egale' : 'Equal payments') : (language === 'ro' ? 'Principal constant' : 'Equal principal')}`,
      ];
      doc.setFontSize(9);
      lines.forEach(l => { doc.text(l, 14, yPos); yPos += 6; });

      const cols = [lbl.month, lbl.payment, lbl.principal, lbl.intCol, lbl.fee, lbl.balance];
      const rows = creditBreakdown.map(r => [r.month, formatMoney(r.payment), formatMoney(r.principal), formatMoney(r.interest), formatMoney(r.fee), formatMoney(r.balance)]);
      autoTable(doc, { head: [cols], body: rows, startY: yPos + 4, theme: 'striped', headStyles: { fillColor: [234, 88, 12] }, styles: { fontSize: 7.5 } });
    } else {
      const lines = [
        `${language === 'ro' ? 'Tip instrument' : 'Instrument type'}: ${typeNames[investmentType]}`,
        `${t('initialDeposit')}: ${formatMoney(inputs.initialDeposit)}`,
        `${t('monthlyContribution')}: ${formatMoney(inputs.monthlyContribution)}`,
        `${t('annualInterestRate')}: ${inputs.annualInterestRate}%`,
        `${t('investmentPeriod')}: ${inputs.years} ${language === 'ro' ? 'ani' : 'years'}`,
      ];
      if (isAccumLike && inputs.adminFeeRate > 0) lines.push(`${t('adminFee')}: ${inputs.adminFeeRate}%`);
      doc.setFontSize(9);
      lines.forEach(l => { doc.text(l, 14, yPos); yPos += 6; });

      const last = yearlyBreakdown[yearlyBreakdown.length - 1];
      if (last) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`${t('finalValue')}: ${formatMoney(last.endBalance)}`, 14, yPos + 3);
        yPos += 10;
      }

      const cols = [lbl.year, lbl.contribs, inputs.includeTaxes ? lbl.interestNet : lbl.interest];
      if (inputs.includeTaxes) cols.push(lbl.tax);
      if (isAccumLike) cols.push(lbl.adminFee);
      if (inputs.topUps?.length) cols.push(lbl.topUp);
      if (inputs.withdrawals?.length) cols.push(lbl.withdraw);
      cols.push(lbl.total);

      const rows = yearlyBreakdown.map(r => {
        const row = [r.year + (r.isPaused ? ' ⏸' : ''), formatMoney(r.totalContributions), formatMoney(r.totalInterestGross - r.totalTaxPaid)];
        if (inputs.includeTaxes) row.push(formatMoney(r.totalTaxPaid));
        if (isAccumLike) row.push(formatMoney(r.adminFee || 0));
        if (inputs.topUps?.length) row.push(r.topUp > 0 ? formatMoney(r.topUp) : '-');
        if (inputs.withdrawals?.length) row.push(r.withdrawal > 0 ? formatMoney(r.withdrawal) : '-');
        row.push(formatMoney(r.endBalance));
        return row;
      });

      autoTable(doc, { head: [cols], body: rows, startY: yPos + 4, theme: 'striped', headStyles: { fillColor: [59, 130, 246] }, styles: { fontSize: 7.5 } });
    }

    const finalY = doc.lastAutoTable?.finalY || 250;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100);
    doc.text(t('taxDisclaimer'), 14, finalY + 8);
    doc.text('moneysmart.ro © 2026', 14, finalY + 14);

    const typeSlug = {
      [INVESTMENT_TYPES.SAVINGS_ACCOUNT]:   language === 'ro' ? 'cont-economii'      : 'savings-account',
      [INVESTMENT_TYPES.ACCUMULATION_PLAN]: language === 'ro' ? 'plan-acumulare'     : 'accumulation-plan',
      [INVESTMENT_TYPES.CHILD_FUTURE]:      language === 'ro' ? 'viitorul-copilului' : 'child-future-plan',
      [INVESTMENT_TYPES.CREDIT]:            language === 'ro' ? 'scadentar-credit'   : 'loan-amortization',
    };
    doc.save(`moneysmart-${typeSlug[investmentType] || 'raport'}.pdf`);
  };

  const sectionTitle = isCredit
    ? (language === 'ro' ? 'Scadențar Credit' : 'Amortization Schedule')
    : (language === 'ro' ? 'Evoluție Anuală Detaliată' : 'Detailed Annual Breakdown');

  return (
    <Card className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden" data-testid="breakdown-table">
      <CardHeader className="pb-4 border-b border-border/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
              <TableIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            {sectionTitle}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV}
              className="h-9 px-4 rounded-full hover:bg-blue-500/10 hover:border-blue-500/50 hover:text-blue-600 transition-all duration-200">
              <Download className="h-4 w-4 mr-2" />{t('exportCSV')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}
              className="h-9 px-4 rounded-full hover:bg-emerald-500/10 hover:border-emerald-500/50 hover:text-emerald-600 transition-all duration-200">
              <FileText className="h-4 w-4 mr-2" />{t('exportPDF')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[420px]">
          {isCredit ? (
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow className="hover:bg-transparent border-b border-border">
                  {[lbl.month, lbl.payment, lbl.principal, lbl.intCol, lbl.fee, lbl.balance].map(h => (
                    <TableHead key={h} className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right first:text-left whitespace-nowrap px-3">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditBreakdown.map((row, i) => {
                  const isLast = i === creditBreakdown.length - 1;
                  return (
                    <TableRow key={row.month} className={isLast ? 'bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 font-semibold' : 'hover:bg-muted/50'}>
                      <TableCell className="font-medium px-3 py-2">{row.month}</TableCell>
                      <TableCell className="text-right tabular-nums px-3 py-2">{formatMoney(row.payment)}</TableCell>
                      <TableCell className="text-right tabular-nums text-blue-600 dark:text-blue-400 px-3 py-2">{formatMoney(row.principal)}</TableCell>
                      <TableCell className="text-right tabular-nums text-red-600 dark:text-red-400 px-3 py-2">{formatMoney(row.interest)}</TableCell>
                      <TableCell className="text-right tabular-nums text-purple-600 dark:text-purple-400 px-3 py-2">{formatMoney(row.fee)}</TableCell>
                      <TableCell className={`text-right tabular-nums px-3 py-2 ${isLast ? 'text-emerald-700 dark:text-emerald-300 font-bold' : ''}`}>{formatMoney(row.balance)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow className="hover:bg-transparent border-b border-border">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 w-16 px-3">{lbl.year}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right px-3">{lbl.contribs}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right px-3">{inputs.includeTaxes ? lbl.interestNet : lbl.interest}</TableHead>
                  {inputs.includeTaxes && <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right px-3">{lbl.tax}</TableHead>}
                  {isAccumLike && <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right px-3">{lbl.adminFee}</TableHead>}
                  {inputs.topUps?.length > 0 && <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right px-3">{lbl.topUp}</TableHead>}
                  {inputs.withdrawals?.length > 0 && <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right px-3">{lbl.withdraw}</TableHead>}
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right px-3">{lbl.total}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {yearlyBreakdown.map((row, index) => {
                  const isLastRow = index === yearlyBreakdown.length - 1;
                  return (
                    <TableRow key={row.year}
                      className={`transition-colors duration-150 ${isLastRow ? 'bg-gradient-to-r from-blue-100 to-emerald-100 dark:from-blue-900/30 dark:to-emerald-900/30 font-semibold' : 'hover:bg-muted/50'} ${row.isPaused ? 'bg-orange-50 dark:bg-orange-900/20' : ''}`}>
                      <TableCell className="font-medium px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {row.year}
                          {row.isPaused && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300"><PauseCircle className="h-2.5 w-2.5 mr-0.5 inline" />{language === 'ro' ? 'Pauză' : 'Paused'}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums px-3 py-2">{formatMoney(row.totalContributions)}</TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400 px-3 py-2">{formatMoney(row.totalInterestGross - row.totalTaxPaid)}</TableCell>
                      {inputs.includeTaxes && <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400 px-3 py-2">{formatMoney(row.totalTaxPaid)}</TableCell>}
                      {isAccumLike && <TableCell className="text-right tabular-nums text-purple-600 dark:text-purple-400 px-3 py-2">{formatMoney(row.adminFee || 0)}</TableCell>}
                      {inputs.topUps?.length > 0 && <TableCell className="text-right tabular-nums text-teal-600 dark:text-teal-400 px-3 py-2">{row.topUp > 0 ? <span className="flex items-center justify-end gap-0.5"><TrendingUp className="h-3 w-3" />{formatMoney(row.topUp)}</span> : '-'}</TableCell>}
                      {inputs.withdrawals?.length > 0 && <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400 px-3 py-2">{row.withdrawal > 0 ? <span className="flex items-center justify-end gap-0.5"><TrendingDown className="h-3 w-3" />{formatMoney(row.withdrawal)}</span> : '-'}</TableCell>}
                      <TableCell className={`text-right tabular-nums px-3 py-2 ${isLastRow ? 'text-blue-700 dark:text-blue-300 font-bold' : ''}`}>{formatMoney(row.endBalance)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default BreakdownTable;
